import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { AuthLayout } from '../layouts/AuthLayout'
import { APP_NAME } from '../config/brand'
import { IconEye, IconEyeOff } from '../components/icons'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { clearSessionMessage, readSessionMessage } from '../lib/sessionMessages'

export default function Login() {
  const { login, isAuthenticated, loading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/'
  const [sessionNotice, setSessionNotice] = useState<string | null>(() => readSessionMessage())
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const message = readSessionMessage()
    if (message) {
      setSessionNotice(message)
      clearSessionMessage()
    }
  }, [])

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    clearError()
    setSessionNotice(null)
    try {
      await login({ email, password })
      navigate(from, { replace: true })
    } catch {
      /* error surfaced via context */
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-primary-foreground shadow-lg">
            {APP_NAME.charAt(0).toUpperCase()}
          </div>
          <h1 className="text-xl font-semibold text-foreground">Sign in to {APP_NAME} admin</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use the administrator email and password your team issued for this console.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="email"
            type="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={1}
            endAdornment={
              <button
                type="button"
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <IconEyeOff className="h-[1.125rem] w-[1.125rem]" aria-hidden />
                ) : (
                  <IconEye className="h-[1.125rem] w-[1.125rem]" aria-hidden />
                )}
              </button>
            }
          />

          <p className="text-right text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Forgot password?
            </Link>
          </p>

          {sessionNotice ? (
            <p
              className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100"
              role="status"
            >
              {sessionNotice}
            </p>
          ) : null}

          {error ? (
            <p
              className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="text-primary hover:underline">
            Back to app
          </Link>
          {' · '}
          If sign-in fails, ask your technical contact to confirm the server address your organisation uses.
        </p>
      </Card>
    </AuthLayout>
  )
}
